import * as React from "react"
import { NavLink } from "react-router"
import { Button, type ButtonProps } from "~/components/ui/button"

export interface LinkProps extends Omit<ButtonProps, "asChild" | "variant"> {
  to: string
  external?: boolean
  children: React.ReactNode
  className?: string
  variant?: ButtonProps["variant"] | "inline"
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      to,
      external = false,
      children,
      variant = "link" as LinkProps["variant"],
      className,
      ...props
    },
    ref,
  ) => {
    // For inline text links, use simple styling without Button wrapper
    if (variant === "inline") {
      const linkClassName =
        "text-accent-foreground underline-offset-4 hover:underline"

      if (external) {
        return (
          <a
            href={to}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
            ref={ref}
          >
            {children}
          </a>
        )
      }

      return (
        <NavLink to={to} className={linkClassName} ref={ref}>
          {children}
        </NavLink>
      )
    }

    // For button-style links, use the existing Button wrapper
    const buttonVariant =
      variant === "inline" ? "link" : (variant as ButtonProps["variant"])

    if (external) {
      return (
        <Button asChild variant={buttonVariant} {...props}>
          <a
            href={to}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
            ref={ref}
          >
            {children}
          </a>
        </Button>
      )
    }

    // For internal navigation, use NavLink to get active state support
    return (
      <Button asChild variant={buttonVariant} {...props}>
        <NavLink to={to} className={className} ref={ref}>
          {children}
        </NavLink>
      </Button>
    )
  },
)

Link.displayName = "Link"

export { Link }
